import React from "react";
import { Calendar, Flag, User2, ListTodo } from "lucide-react";
import {
  EntityGrid,
  EntityCard,
  StatusPill,
  IconButton,
} from "../../../design-system";
import styles from "./TaskCardGrid.module.scss";

const PRIORITY_COLOR = {
  high: "red",
  middle: "yellow",
  low: "emerald",
};

const STATUS_COLOR = {
  "To Do": "sky",
  "In Progress": "violet",
  Completed: "emerald",
  Done: "emerald",
};

const STATUS_PILL_MAP = Object.fromEntries(
  Object.entries(STATUS_COLOR).map(([status, color]) => [
    status,
    {
      bg: `var(--cal-${color}-soft)`,
      fg: `var(--cal-${color}-fg)`,
      border: "transparent",
    },
  ])
);

/**
 * Tasks kart görünümü — list-2 / grid-3 / grid-4 modları için. List-1
 * modu mevcut ListTable (DataTable) ile gösterilir; bu bileşen yalnızca
 * kart-tipi görünümlerde devreye girer.
 */
const TaskCardGrid = ({ tasks = [], viewMode = "grid-3", onOpen, onEdit }) => {
  const density = viewMode === "list-2" ? "compact" : "comfortable";

  return (
    <EntityGrid
      mode={viewMode}
      emptyState={
        <>
          <ListTodo size={32} className={styles.emptyIcon} aria-hidden />
          <span>Görev yok — yukarıdan ekleyebilirsin.</span>
        </>
      }
    >
      {tasks.map((task) => {
        const priorityColor = PRIORITY_COLOR[task.priority] || "graphite";
        const tagList = Array.isArray(task.labels)
          ? task.labels
          : task.labels
          ? [task.labels]
          : [];

        const metaItems =
          density === "comfortable"
            ? [
                {
                  label: "Durum",
                  value: (
                    <StatusPill
                      value={task.status || "To Do"}
                      colorMap={STATUS_PILL_MAP}
                      interactive={false}
                      size="xs"
                    />
                  ),
                },
                {
                  label: "Öncelik",
                  value: (
                    <span
                      className={styles.priorityValue}
                      data-color={priorityColor}
                    >
                      {task.priority || "—"}
                    </span>
                  ),
                },
                task.assignedTo
                  ? {
                      label: "Atanan",
                      value: (
                        <span className={styles.metaInlineIcon}>
                          <User2 size={12} aria-hidden /> {task.assignedTo}
                        </span>
                      ),
                    }
                  : null,
                task.date
                  ? {
                      label: "Tarih",
                      value: (
                        <span className={styles.metaInlineIcon}>
                          <Calendar size={12} aria-hidden /> {task.date}
                        </span>
                      ),
                    }
                  : null,
              ].filter(Boolean)
            : null;

        return (
          <EntityCard
            key={task.id}
            density={density}
            onClick={() => onOpen?.(task)}
            accentColor={priorityColor}
            icon={<ListTodo size={18} />}
            title={task.title || "Adsız görev"}
            subtitle={task.project || task.relatedTo}
            actions={
              onEdit ? (
                <IconButton
                  size="sm"
                  variant="ghost"
                  ariaLabel="Düzenle"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(task);
                  }}
                >
                  <Flag size={14} />
                </IconButton>
              ) : null
            }
            meta={metaItems}
            tags={
              tagList.length > 0 ? (
                <>
                  {tagList.slice(0, 3).map((label) => (
                    <span key={label} className={styles.tagChip}>
                      {label}
                    </span>
                  ))}
                  {tagList.length > 3 ? (
                    <span className={styles.tagOverflow}>
                      +{tagList.length - 3}
                    </span>
                  ) : null}
                </>
              ) : null
            }
            footer={
              density === "comfortable" ? (
                <>
                  <span className={styles.footerId}>#{task.id}</span>
                  {task.points ? (
                    <span className={styles.footerPoints}>
                      {task.points} puan
                    </span>
                  ) : null}
                </>
              ) : null
            }
          />
        );
      })}
    </EntityGrid>
  );
};

export default TaskCardGrid;
